/**
 * Status metadata from `common/status-metadata` — lookup keys, group resolution, inline styles.
 */

export const normalizeStatusLookupKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalizePageName = (pageName) =>
  String(pageName ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

export const normalizeStatusMetadataAccessor = (accessor) =>
  String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");

const normalizeAccessor = normalizeStatusMetadataAccessor;

/** All Candidates list — columns that use `common/status-metadata` colors. */
export const CANDIDATE_STATUS_METADATA_ACCESSOR_KEYS = new Set([
  "profilestatus",
  "orderstatus",
  "mecstatus",
  "drugstatus",
  "updatesend",
  "fadvsend",
  "isfadvsent",
  "funnelstatus",
  "funnlstatus",
  "onboardingstatus",
]);

export const isCandidateStatusMetadataAccessor = (accessor) =>
  CANDIDATE_STATUS_METADATA_ACCESSOR_KEYS.has(normalizeAccessor(accessor));

/** Booleans / Yes-No fields → metadata `yes-no` keys; status text passes through. */
export const normalizeCandidateStatusMetadataValue = (accessor, rawValue) => {
  const acc = normalizeAccessor(accessor);
  if (!["fadvsend", "isfadvsent", "updatesend"].includes(acc)) {
    return rawValue;
  }
  if (rawValue === null || rawValue === undefined || rawValue === "") return rawValue;
  if (typeof rawValue === "boolean") return rawValue ? "yes" : "no";
  const normalized = String(rawValue).trim().toLowerCase();
  if (normalized === "true" || normalized === "1" || normalized === "y") return "yes";
  if (normalized === "false" || normalized === "0" || normalized === "n") return "no";
  if (normalized === "yes" || normalized === "no") return normalized;
  return rawValue;
};

const isCallExecutivePage = (page) =>
  page.includes("callexecutive") ||
  page === "callaudit" ||
  page.includes("callbackrequest");

const isCandidateListPage = (page) =>
  page.includes("candidate") ||
  page.includes("verifytest") ||
  page.includes("modifyclosure") ||
  page.includes("fadv");

const isWeeklyReviewPage = (page) =>
  page.includes("weeklyreview") || page.includes("clientreview");

/**
 * Maps a table column accessor (+ optional page) to API `groupKey`.
 */
export const resolveStatusMetadataGroupKey = (accessor, pageName = "") => {
  const acc = normalizeAccessor(accessor);
  const page = normalizePageName(pageName);

  if (acc === "orderstatus") return "order-status";
  if (acc === "profilestatus") return "profile-status";
  if (acc === "onboardingstatus") return "profile-status";
  if (acc === "callstatus" || acc === "statuslabel") return "call-status";
  if (acc === "drugstatus") return "drug-test-status";
  if (acc === "mecstatus") return "drug-test-status";
  if (acc === "readytosendstatus" || acc === "readytosend") return "ready-to-send-status";
  if (acc === "fadvsend" || acc === "isfadvsent" || acc === "updatesend") return "yes-no";
  if (acc === "funnelstatus" || acc === "funnlstatus") return "order-status";
  if (acc === "incentive") return "incentive-type";
  if (acc === "jobacceptance") return "yes-no";
  if (acc === "reviewstatus") return "client-review-status";

  if (acc === "laststatus") {
    if (isCallExecutivePage(page) || page.includes("call")) return "call-status";
    return "call-status";
  }

  if (acc === "apistatus") return "order-status";

  if (
    acc === "statusthisweek" ||
    acc === "statuslastweek" ||
    acc === "thisweekstatus" ||
    acc === "lastweekstatus"
  ) {
    return "client-review-status";
  }

  if (acc === "status" || acc === "statusid" || acc === "statustitle") {
    if (isWeeklyReviewPage(page)) return "client-review-status";
    if (page.includes("clientreview")) return "client-review-status";
    if (isCallExecutivePage(page)) return "call-status";
    if (isCandidateListPage(page)) return "profile-status";
    if (page.includes("hire")) return "profile-status";
    if (page.includes("fadv")) return "order-status";
    if (page.includes("orderlog")) return "order-status";
    if (page.includes("attendance")) return "entity-status";
    if (
      page.includes("client") ||
      page.includes("team") ||
      page.includes("manageuser") ||
      page.includes("companyuser") ||
      page.includes("usertype") ||
      page.includes("workshift") ||
      page.includes("job") ||
      page.includes("source") ||
      page.includes("reason") ||
      page.includes("onboardingdocument") ||
      page.includes("mailtemplate")
    ) {
      return "entity-status";
    }
    return "entity-status";
  }

  if (
    [
      "quota",
      "forclient",
      "forjob",
      "fortools",
      "candidatedeactive",
      "isallowed",
      "approved",
      "online",
      "blocked",
      "isregister",
      "teamrequired",
      "uniqueclient",
      "multipleteam",
      "isbackend",
      "isforuscitizens",
      "isfornonuscitizens",
      "isdefault",
      "dataprocessed",
    ].includes(acc)
  ) {
    return "yes-no";
  }

  if (statusKeyReturnsStatus(accessor)) return "entity-status";

  return null;
};

/** Mirrors `statusKey()` in GeneralHelper for group fallback. */
const statusKeyReturnsStatus = (accessor) => {
  const item = String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");
  return (
    item === "questiontypestatus" ||
    item === "jobcategorystatus" ||
    item === "jobrequirestatus" ||
    item === "status" ||
    item === "statustitle"
  );
};

export const buildStatusMetadataIndex = (items = []) => {
  const list = Array.isArray(items) ? items : [];
  const byGroupAndKey = new Map();
  const byGroupAndLegacyId = new Map();
  const byGroupAndTitle = new Map();
  const byGroupAndNormalizedKey = new Map();

  list.forEach((item) => {
    const groupKey = item?.groupKey;
    if (!groupKey) return;

    const keyNorm = normalizeStatusLookupKey(item?.key);
    if (keyNorm) byGroupAndKey.set(`${groupKey}::${keyNorm}`, item);

    if (item?.legacyId !== null && item?.legacyId !== undefined) {
      byGroupAndLegacyId.set(`${groupKey}::${String(item.legacyId)}`, item);
    }

    const titleNorm = normalizeStatusLookupKey(item?.title);
    if (titleNorm) byGroupAndTitle.set(`${groupKey}::${titleNorm}`, item);

    const normalizedKey = normalizeStatusLookupKey(item?.normalizedKey);
    if (normalizedKey) byGroupAndNormalizedKey.set(`${groupKey}::${normalizedKey}`, item);
  });

  return {
    items: list,
    byGroupAndKey,
    byGroupAndLegacyId,
    byGroupAndTitle,
    byGroupAndNormalizedKey,
  };
};

const lookupInGroup = (index, groupKey, rawValue) => {
  if (!groupKey || rawValue === null || rawValue === undefined) return null;

  const trimmed = String(rawValue).trim();
  if (!trimmed && rawValue !== 0 && rawValue !== "0") return null;

  const normalized = normalizeStatusLookupKey(trimmed);

  const fromKey = index.byGroupAndKey.get(`${groupKey}::${normalized}`);
  if (fromKey) return fromKey;

  const fromTitle = index.byGroupAndTitle.get(`${groupKey}::${normalized}`);
  if (fromTitle) return fromTitle;

  const fromNormalized = index.byGroupAndNormalizedKey.get(`${groupKey}::${normalized}`);
  if (fromNormalized) return fromNormalized;

  const fromLegacy = index.byGroupAndLegacyId.get(`${groupKey}::${trimmed}`);
  if (fromLegacy) return fromLegacy;

  return null;
};

/**
 * Find metadata row for a cell value. Tries scoped group first, then global key match.
 */
export const lookupStatusMetadataItem = (index, groupKey, rawValue) => {
  if (!index?.items?.length) return null;
  if (rawValue === null || rawValue === undefined) return null;

  const trimmed = String(rawValue).trim();
  if (!trimmed && rawValue !== 0 && rawValue !== "0") return null;

  if (groupKey) {
    const scoped = lookupInGroup(index, groupKey, rawValue);
    if (scoped) return scoped;
  }

  const normalized = normalizeStatusLookupKey(trimmed);

  for (const item of index.items) {
    if (normalizeStatusLookupKey(item?.key) === normalized) return item;
    if (normalizeStatusLookupKey(item?.title) === normalized) return item;
    if (normalizeStatusLookupKey(item?.normalizedKey) === normalized) return item;
    if (item?.legacyId !== null && String(item.legacyId) === trimmed) return item;
  }

  return null;
};

/** Parse #rgb / #rrggbb / rgb() / rgba() → [r,g,b] 0–255, or null. */
const parseCssColorToRgb = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const hex = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1];
    if (h.length === 3) {
      return [
        parseInt(h[0] + h[0], 16),
        parseInt(h[1] + h[1], 16),
        parseInt(h[2] + h[2], 16),
      ];
    }
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }

  const rgb = raw.match(
    /^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)/i
  );
  if (rgb) {
    return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  }

  return null;
};

const channelToLinear = (channel) => {
  const c = Math.max(0, Math.min(255, Number(channel))) / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

const getRelativeLuminance = (cssColor) => {
  const rgb = parseCssColorToRgb(cssColor);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(channelToLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrastRatio = (lumA, lumB) => {
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
};

export const getStatusMetadataInlineStyle = (item) => {
  if (!item) return null;
  let backgroundColor = item.backgroundColor;
  let color = item.textColor;
  if (!backgroundColor && !color) return null;

  // Keep badge text readable when API colors are low-contrast (e.g. light on light).
  if (backgroundColor) {
    const bgLum = getRelativeLuminance(backgroundColor);
    if (bgLum !== null) {
      const textLum = color ? getRelativeLuminance(color) : null;
      const ratio =
        textLum !== null ? contrastRatio(bgLum, textLum) : 0;
      if (!color || ratio < 3) {
        color = bgLum > 0.55 ? "#1e293b" : "#ffffff";
      }
    }
  }

  return {
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(color ? { color, fontWeight: 600 } : {}),
  };
};

export const getStatusMetadataDisplayLabel = (item, rawValue) => {
  const trimmed = String(rawValue ?? "").trim();
  if (item?.title) return String(item.title);
  return trimmed;
};
